import { ImportedData, ImportedDataType } from './ImportedData.types';
import { ImportedDataService } from './ImportedData.service';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { ImportedDataRequestService } from './ImportedDataRequest.service';
import { IImportedDataRequest } from './ImportedDataRequest.types';

type CacheEntry = {
  data: ImportedData;
  expiresAt: number;
};

const importedDataCache = new Map<string, CacheEntry>();

export class ImportedDataProvider {
  constructor(
    private readonly _importedDataService: ImportedDataService,
    private readonly _cacheDuration: number,
    private readonly _importedDataRequestService: ImportedDataRequestService
  ) {}

  private getCacheKey(slug: string, dataType: ImportedDataType): string {
    return `${slug}:${dataType}`;
  }

  private getRequestCacheKey(request: IImportedDataRequest): string {
    return `${request.cacheKey}:${request.siteName}`;
  }

  public async getDataFor(slug: string, dataType: ImportedDataType): Promise<ImportedData> {
    if (sparkleUtils.isEmptyOrWhiteSpace(slug)) {
      return {} as ImportedData;
    }

    const cacheKey = this.getCacheKey(slug, dataType);

    const now = Date.now();

    const cached = importedDataCache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      return cached.data;
    }

    const data = (await this._importedDataService.getData(slug, dataType)) ?? ({} as ImportedData);

    if (this.isEmptyData(data)) {
      return {} as ImportedData;
    }

    importedDataCache.set(cacheKey, {
      data,
      expiresAt: now + this._cacheDuration * 1000,
    });

    return data;
  }

  public async getDataByRequest(request: IImportedDataRequest): Promise<ImportedData> {
    if (!request || request.isInvalid) {
      return {} as ImportedData;
    }

    const cacheKey = this.getRequestCacheKey(request);
    const now = Date.now();
    const cached = importedDataCache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      return cached.data;
    }

    const data =
      (await this._importedDataRequestService.getDataByRequest(request)) ?? ({} as ImportedData);

    if (this.isEmptyData(data)) {
      return {} as ImportedData;
    }

    importedDataCache.set(cacheKey, {
      data,
      expiresAt: now + this._cacheDuration * 1000,
    });

    return data;
  }

  private isEmptyData(data: ImportedData): boolean {
    return !data || Object.keys(data).length === 0;
  }
}
